import { DEFAULT_ORDER_STATUS } from "@bcc/components/orders/constants";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { PurchaseOrder } from "@shared/types";
import { useMemo } from "react";
import { useDemoDataModule } from "./use-demo-data-module";

const loadOrdersDemo = () => import("./orders.demo");

const FALLBACK_ACCOUNT_NUMBER = "DEMO-ACCOUNT";
const FALLBACK_COUNTRY_CODE = "US";

export interface DemoOrdersView {
  orders: PurchaseOrder[];
  statusCounts: Record<string, number>;
  /** True for the one tick while the fixtures load */
  isLoading: boolean;
}

/**
 * Sample orders for the orders table while the tour runs; undefined in normal use, so callers
 * fall back to the real data. `orderStatusParam` is the page's `?orderStatus=` value.
 */
export function useDemoOrders(orderStatusParam: string | null): DemoOrdersView | undefined {
  const { isActive, module } = useDemoDataModule(loadOrdersDemo);
  const [{ currentVendor, customerAccountCarrierDetails }] = useVendorContext();
  // A carrier binding can list several comma-separated accounts; any one makes the rows resolve a carrier
  const customerAccountNumber = customerAccountCarrierDetails?.[0]?.customerAccountNumber.split(",")[0]?.trim() ?? FALLBACK_ACCOUNT_NUMBER;
  const countryCode = currentVendor?.countryCode ?? FALLBACK_COUNTRY_CODE;

  const allOrders = useMemo(
    () => module?.buildDemoOrders({ customerAccountNumber, countryCode }),
    [module, customerAccountNumber, countryCode]
  );

  return useMemo(() => {
    if (!isActive) return undefined;
    if (!module || !allOrders) return { orders: [], statusCounts: {}, isLoading: true };
    return {
      orders: module.filterDemoOrders(allOrders, orderStatusParam ?? DEFAULT_ORDER_STATUS),
      statusCounts: module.buildDemoStatusCounts(allOrders),
      isLoading: false,
    };
  }, [isActive, module, allOrders, orderStatusParam]);
}
